// Home v2 — responsive app shell. Reusable chrome for any screen.
//  - Desktop: sticky Sidebar + a full-height content area. The content itself
//    owns scrolling so the right column can stay fixed while the middle scrolls.
//  - Tablet (stacked): Sidebar + one ScrollView (whole content scrolls).
//  - Mobile: ScrollView only; the bottom tab bar is global (MobileTabBar).
import React from 'react'
import { ScrollView, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import useTheme from '@common/hooks/useTheme'
import { Mode } from '@web/contexts/appModeContext'

import { HomeLayout } from '../useHomeBreakpoints'
import { NavItem as NavItemType, NavKey } from '../navConfig'
import { LAYOUT } from '../tokens'
import Sidebar from './Sidebar'

type Props = {
  active: NavKey
  mode: Mode
  layout: HomeLayout
  onNavigate: (item: NavItemType) => void
  children: React.ReactNode
}

const AppShell: React.FC<Props> = ({ active, mode, layout, onNavigate, children }) => {
  const { theme } = useTheme()
  // iOS home-screen PWA (standalone) reports a top notch/Dynamic-Island inset
  // and a bottom home-indicator inset. In a normal browser tab these are 0, so
  // adding them is a no-op there. react-native-safe-area-context reads them from
  // env(safe-area-inset-*) on web (needs viewport-fit=cover, already set).
  const insets = useSafeAreaInsets()
  const { isMobile, isStacked, isDesktop } = layout
  const bg = theme.homePageBackground as string

  const sidebar = <Sidebar active={active} mode={mode} onNavigate={onNavigate} />


  // Desktop: no page-level scroll. The screen renders its own left/right scroll
  // model so the right column stays put while the middle scrolls.
  if (isDesktop) {
    return (
      <View style={{ flex: 1, flexDirection: 'row', backgroundColor: bg }}>
        {sidebar}
        <View style={{ flex: 1 }}>{children}</View>
      </View>
    )
  }

  const mainPadding = isMobile ? 16 : 20
  const scroll = (
    <ScrollView
      style={{ flex: 1, backgroundColor: bg }}
      contentContainerStyle={{
        paddingHorizontal: mainPadding,
        paddingTop: (isMobile ? 16 : 32) + insets.top,
        paddingBottom: (isMobile ? LAYOUT.bottomTabHeight + 32 : 32) + insets.bottom,
        alignItems: 'center'
      }}
      showsVerticalScrollIndicator={false}
    >
      <View style={{ width: '100%', maxWidth: LAYOUT.gridLeftWidth }}>{children}</View>
    </ScrollView>
  )

  if (isStacked) {
    return (
      <View style={{ flex: 1, flexDirection: 'row', backgroundColor: bg }}>
        {sidebar}
        {scroll}
      </View>
    )
  }

  // mobile — the bottom tab bar is rendered globally in Router.web (MobileTabBar)
  // so it persists across tab navigations instead of remounting per screen.
  return <View style={{ flex: 1, backgroundColor: bg }}>{scroll}</View>
}

export default React.memo(AppShell)
